import React from 'react';
import { PLANET_DATA, PLANET_POSITIONS, MAP_PATH } from '../../data/planets';
import { STARS } from './stars';

// ─── Individual Planet Button ─────────────────────────────────────────────────
function PlanetButton({ planet, pos, isUnlocked, isCompleted, isNewlyUnlocked, progress, onClick }) {
  return (
    <div
      className="sq-planet-wrapper"
      style={{ left: `${pos.cx}%`, top: `${pos.cy}%` }}
    >
      {/* Progress dots (above planet, only for unlocked + incomplete) */}
      {isUnlocked && !isCompleted && (
        <div className="sq-planet-progress">
          {planet.questions.map((_, qi) => (
            <span
              key={qi}
              className={`sq-prog-dot ${qi < progress ? 'done' : ''}`}
            />
          ))}
        </div>
      )}

      {/* The planet circle */}
      <button
        className={`sq-planet-btn ${isUnlocked ? 'unlocked' : 'locked'} ${isNewlyUnlocked ? 'newly-unlocked' : ''}`}
        onClick={isUnlocked ? onClick : undefined}
        disabled={!isUnlocked}
        aria-label={isUnlocked ? `פתח את ${planet.name}` : `${planet.name} נעול`}
        style={
          isUnlocked
            ? {
                background: `radial-gradient(circle at 35% 30%, ${planet.gradTo}, ${planet.gradFrom})`,
                boxShadow: `0 0 18px 4px rgba(${planet.glowRgb},0.5), 0 0 36px 8px rgba(${planet.glowRgb},0.25)`,
              }
            : undefined
        }
      >
        <span className="sq-planet-emoji">{planet.emoji}</span>

        {/* Lock icon overlay for locked planets */}
        {!isUnlocked && (
          <span className="sq-lock-icon" aria-hidden="true">🔒</span>
        )}
      </button>

      {/* Completed badge */}
      {isCompleted && (
        <div className="sq-completed-badge" aria-label="הושלם">✓</div>
      )}

      {/* Planet name label */}
      <div className={`sq-planet-label ${isUnlocked ? '' : 'locked-label'}`}>
        {planet.name}
      </div>
    </div>
  );
}

// ─── Galaxy Map ───────────────────────────────────────────────────────────────
export default function GalaxyMap({ unlockedUpTo, completedPlanets, planetProgress, newlyUnlocked, onPlanetClick }) {
  return (
    <div className="sq-map">
      {/* ── Starfield (SVG layer) ── */}
      <svg className="sq-stars-svg" aria-hidden="true">
        {STARS.map((s) => (
          <circle
            key={s.id}
            cx={`${s.x}%`}
            cy={`${s.y}%`}
            r={s.r}
            fill="white"
            opacity={s.opacity}
            className={s.twinkle ? 'sq-twinkle' : undefined}
            style={s.twinkle ? { animationDelay: s.delay, animationDuration: s.duration } : undefined}
          />
        ))}
      </svg>

      {/* ── Ambient nebula blobs ── */}
      <div className="sq-nebula sq-nebula-1" aria-hidden="true" />
      <div className="sq-nebula sq-nebula-2" aria-hidden="true" />
      <div className="sq-nebula sq-nebula-3" aria-hidden="true" />

      {/* ── Path connecting planets ── */}
      <svg className="sq-path-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {/* Grey dashed base path */}
        <path
          d={MAP_PATH}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.6"
          strokeDasharray="2.5 2.5"
        />
        {/* Glowing completed segment up to unlockedUpTo */}
        {unlockedUpTo > 0 && (
          <path
            d={MAP_PATH}
            fill="none"
            stroke="rgba(147,197,253,0.45)"
            strokeWidth="0.9"
            strokeDasharray={`${unlockedUpTo * 22} 200`}
            strokeLinecap="round"
          />
        )}
      </svg>

      {/* ── Planets ── */}
      {PLANET_DATA.map((planet, i) => (
        <PlanetButton
          key={planet.id}
          planet={planet}
          pos={PLANET_POSITIONS[i]}
          isUnlocked={i <= unlockedUpTo}
          isCompleted={completedPlanets.has(i)}
          isNewlyUnlocked={newlyUnlocked === i}
          progress={planetProgress[i] || 0}
          onClick={() => onPlanetClick(i)}
        />
      ))}
    </div>
  );
}
